import { maybe } from '@passionware/monads';
import { SimpleReadOnlyEvent } from '@passionware/simple-event';
import { QueryClient, useQueries, useQuery } from '@tanstack/react-query';
import zipObject from 'lodash/zipObject';
import { Callout } from '../../../../__types__/v1/models/Callout';
import { createBatcher } from '../../../../v4/entities/common/batcher';
import { V1Action } from '../../../../v4/migration/actionAsEventMiddleware';
import { titleToCalloutSlug } from '../../../api/callouts/callout-slug.logic';
import {
  CalloutAssociations,
  CalloutsApi,
  CalloutsListQuery,
  CalloutsQuery
} from '../../../api/callouts/callouts.api';
import { asGlobalCalloutsQuery } from '../../../api/callouts/callouts.api';
import { ensureIdleQuery } from '../../../platform/ensureIdleQuery';
import { WithServices } from '../../../platform/ts/services';
import { WithErrorService } from '../../front/ErrorService/ErrorService';
import { WithMessageService } from '../../internal/MessageService/MessageService';
import { CalloutsService } from './CalloutsService';

function normalizeCalloutsListQuery(query: CalloutsListQuery): CalloutsQuery {
  if ('workspaceId' in query) {
    return query;
  }
  return asGlobalCalloutsQuery(query);
}

export function createCalloutsService(
  config: WithServices<[WithErrorService, WithMessageService]> & {
    api: CalloutsApi;
    queryClient: QueryClient;
    v1Event: SimpleReadOnlyEvent<V1Action>;
  }
): CalloutsService {
  const {
    api,
    queryClient,
    v1Event,
    services: { errorService, messageService }
  } = config;
  const interceptor = errorService.withCategory('CalloutsService');

  // Subscribe to system effects to handle cache invalidation on mutations
  messageService.reportSystemEffect.subscribeToRequest(async message => {
    for (const effect of message.request.scope) {
      switch (effect.entity) {
        case 'callout': {
          // Invalidate all callout list queries
          queryClient.removeQueries({
            queryKey: ['callouts-v5', 'list'],
            type: 'inactive'
          });
          await queryClient.invalidateQueries({
            queryKey: ['callouts-v5', 'list'],
            type: 'active'
          });
          // Invalidate the specific item query
          await queryClient.invalidateQueries({
            queryKey: ['callouts-v5', 'item', effect.entityId]
          });
          // Invalidate associations if applicable
          await queryClient.invalidateQueries({
            queryKey: ['callouts-v5', 'associations', effect.entityId]
          });
        }
      }
    }
    message.sendResponse();
  });

  // Listen to v1 Redux actions and sync to TanStack Query cache
  v1Event.addListener(async event => {
    // Actions that require invalidating all caches (complex changes or deletions)
    switch (event.type) {
      case 'BATCH_ARCHIVE_CALLOUTS_SUCCESS':
      case 'BATCH_UNARCHIVE_CALLOUTS_SUCCESS':
      case 'DELETE_CALLOUT_SUCCESS':
      case 'DELETE_APPLICANT_SUCCESS':
        await queryClient.invalidateQueries({
          queryKey: ['callouts-v5']
        });
        return;
    }

    // Actions that provide fresh data to update specific cache entries
    switch (event.type) {
      case 'GET_CALLOUT_SUCCESS':
        queryClient.setQueryData(
          ['callouts-v5', 'item', event.result.id],
          event.result
        );
        return;

      case 'CREATE_CALLOUT_SUCCESS':
      case 'UPDATE_CALLOUT_SUCCESS':
        queryClient.setQueryData(
          ['callouts-v5', 'item', event.result.id],
          event.result
        );
        await queryClient.invalidateQueries({
          queryKey: ['callouts-v5', 'list']
        });
        return;

      case 'GET_CALLOUT_ASSOCIATIONS_SUCCESS':
        queryClient.setQueryData(
          ['callouts-v5', 'associations', event.callout_id],
          event.result
        );
        return;

      case 'GET_PUBLIC_CALLOUT_SUCCESS':
        queryClient.setQueryData(
          ['callouts-v5', 'item-public', event.result.callout.id],
          event.result
        );
        return;
    }
  });

  // Batcher for associations
  const associationsBatcher = createBatcher<
    Callout['id'],
    CalloutAssociations[],
    CalloutAssociations | null
  >(
    async ids => {
      if (ids.length === 0) {
        return [];
      }
      return await interceptor.wrap(() => api.getBatchAssociations(ids))();
    },
    (response, id) => {
      // Find the associations for this callout ID (ids were added back to response)
      return response.find(assoc => assoc.id === id) ?? null;
    }
  );

  const ensureList: CalloutsService['ensureList'] = query =>
    queryClient.fetchQuery({
      queryKey: ['callouts-v5', 'list', query],
      queryFn: interceptor.wrap(ctx =>
        api.getList(normalizeCalloutsListQuery(query), ctx.signal)
      )
    });

  const isSlugOccupied: CalloutsService['isSlugOccupied'] = async (
    slug,
    options
  ) => {
    const baseQuery = {
      search: '',
      sort: null,
      page: { page: 0, pageSize: 1 },
      filters: {
        slug: { operator: 'equals' as const, value: slug }
      }
    };
    const listQuery: CalloutsListQuery =
      options?.workspaceId != null
        ? { ...baseQuery, workspaceId: options.workspaceId }
        : baseQuery;

    const result = await ensureList(listQuery);

    const existing = result.items[0];
    if (!existing) {
      return false;
    }
    if (options?.excludeCalloutId && existing.id === options.excludeCalloutId) {
      return false;
    }
    return true;
  };

  const resolveUniqueSlug: CalloutsService['resolveUniqueSlug'] = async (
    baseSlug,
    options
  ) => {
    const normalizedBase = titleToCalloutSlug(baseSlug) || 'callout';

    for (let attempt = 0; attempt < 100; attempt += 1) {
      const candidate =
        attempt === 0 ? normalizedBase : `${normalizedBase}-${attempt + 1}`;

      const occupied = await isSlugOccupied(candidate, options);
      if (!occupied) {
        return candidate;
      }
    }

    throw new Error('Could not generate a unique callout slug');
  };

  return {
    useList: query =>
      ensureIdleQuery(
        query,
        useQuery(
          {
            queryKey: ['callouts-v5', 'list', query],
            queryFn: interceptor.wrap(async ctx => {
              const result = await api.getList(
                normalizeCalloutsListQuery(query!),
                ctx.signal
              );
              // Populate detail cache with callouts from list response
              for (const callout of result.items) {
                queryClient.setQueryData(
                  ['callouts-v5', 'item', callout.id],
                  callout
                );
              }
              return result;
            }),
            enabled: maybe.isPresent(query)
          },
          queryClient
        )
      ),
    ensureList,
    isSlugOccupied,
    resolveUniqueSlug,
    useItem: id =>
      ensureIdleQuery(
        id,
        useQuery(
          {
            queryKey: ['callouts-v5', 'item', id],
            queryFn: interceptor.wrap(async ctx => {
              if (!id) {
                throw new Error('Callout ID is required');
              }
              return await api.getItem(id, ctx.signal);
            }),
            enabled: maybe.isPresent(id)
          },
          queryClient
        )
      ),
    ensureItem: id =>
      queryClient.fetchQuery({
        queryKey: ['callouts-v5', 'item', id],
        queryFn: interceptor.wrap(ctx => api.getItem(id, ctx.signal))
      }),
    ensureAssociations: id =>
      queryClient.fetchQuery({
        queryKey: ['callouts-v5', 'associations', id],
        queryFn: interceptor.wrap(ctx => api.getAssociations(id, ctx.signal))
      }),
    useItemPublic: (id, query = {}) =>
      ensureIdleQuery(
        id,
        useQuery(
          {
            queryKey: ['callouts-v5', 'item-public', id, query],
            queryFn: interceptor.wrap(async ctx => {
              if (!id) {
                throw new Error('Callout ID is required');
              }
              return await api.getItemPublic(id, query, ctx.signal);
            }),
            enabled: maybe.isPresent(id)
          },
          queryClient
        )
      ),
    ensureItemPublic: (id, query = {}) =>
      queryClient.fetchQuery({
        queryKey: ['callouts-v5', 'item-public', id, query],
        queryFn: interceptor.wrap(ctx =>
          api.getItemPublic(id, query, ctx.signal)
        )
      }),
    useAssociationsById: ids => {
      // Always call useQueries (hooks must be called unconditionally)

      // Use useQueries with individual association queries
      const results = useQueries(
        {
          queries: ids.map(id => ({
            queryKey: ['callouts-v5', 'associations', id],
            queryFn: async ({ signal }: { signal: AbortSignal }) => {
              const batched = await associationsBatcher.fetch({
                payload: id,
                signal
              });
              if (!batched) {
                throw new Error(`Associations for callout ${id} not found`);
              }
              return batched;
            },
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 10 * 60 * 1000 // 10 minutes
          }))
        },
        queryClient
      );

      // Convert UseQueryResult to Record<id, RemoteData<CalloutAssociations>>
      return zipObject(ids, results);
    }
  };
}
